<script lang="ts">
  /**
   * One party of a conversation, named and linked to their Skool profile.
   *
   * THREE STATES, and all three are worth having. A person we can name and reach is a
   * link; a person we can name but not reach is plain text; a person we can do neither
   * for is stated as unknown rather than left blank — "we do not know who sent these"
   * is information, and an empty slot just reads as a gap in the layout.
   *
   * `tone` matches the bubbles below, so a header of two of these reads as a KEY: the
   * owner in the same colour their messages are, the counterpart in the same colour
   * theirs are. It also names the unknown case, since an unnameable owner and an
   * unnameable counterpart are different findings.
   *
   * THE LINK GOES THROUGH THE HOST, which is why this is here and not in the page that
   * first drew it. `followLink` means the admin app opens a tab and the Helper's side
   * panel points the tab beside it at Skool instead of navigating itself away — the
   * same rule every other Skool link in this package already follows. `target="_blank"`
   * stays on the anchor as the fallback and as what makes cmd-click honest.
   */
  import Avatar from './Avatar.svelte';
  import { followLink } from './host';

  let {
    name = null,
    handle = null,
    href = null,
    pictureUrl = null,
    tone = 'counterpart',
  }: {
    name?: string | null;
    handle?: string | null;
    /** Their Skool profile. Null where we hold no handle to build one from. */
    href?: string | null;
    /** Their face, where the route sends one. Omitted rather than faked: a name with
     *  no avatar reads fine, an avatar of the wrong person does not. */
    pictureUrl?: string | null;
    tone?: 'owner' | 'counterpart';
  } = $props();

  /** Their name if Skool gave us one, their handle if not. */
  const label = $derived(name ?? (handle ? '@' + handle : null));
  const toneCls = $derived(tone === 'owner' ? 'text-primary' : 'text-base-content');
</script>

{#if href && label}
  <a
    {href}
    target="_blank"
    rel="noopener noreferrer"
    onclick={(e) => followLink(e, href)}
    class="inline-flex max-w-[16rem] items-center gap-1.5 truncate font-semibold underline-offset-2 hover:underline {toneCls}"
    title="Open {label} on Skool"
  >
    <Avatar src={pictureUrl} name={label} size={20} />
    <span class="redact truncate">{label}</span>
    <svg class="h-3 w-3 shrink-0 opacity-60" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M7 17 17 7" /><polyline points="7 7 17 7 17 17" /></svg>
  </a>
{:else if label}
  <span class="inline-flex max-w-[16rem] items-center gap-1.5 truncate font-semibold {toneCls}">
    <Avatar src={pictureUrl} name={label} size={20} />
    <span class="redact truncate">{label}</span>
  </span>
{:else}
  <span class="font-medium text-base-content/45">unknown {tone}</span>
{/if}
